import React, { useState, useEffect } from "react";
import { X, Trash } from "lucide-react";
import { toast } from "react-toastify";
import { apiputFormData } from "../../API/Api_Url";

function ContractorUpdateTrackerModal({ isOpen, onClose, fetchAllTracker, tracker }) {
    const [Title, setTitle] = useState("");
    const [Description, setDescription] = useState("");
    const [Status, setStatus] = useState("");
    const [StartDate, setStartDate] = useState("");
    const [EndDate, setEndDate] = useState("");
    const [Image, setImage] = useState([]);
    const [Video, setVideo] = useState([]);
    const [ExistingMedia, setExistingMedia] = useState([]);
    const [Loading, setLoading] = useState(false);

    useEffect(() => {
        if (tracker) {
            setTitle(tracker?.Title || "");
            setDescription(tracker?.Description || "");
            setStatus(tracker?.Status || "");
            setStartDate(tracker?.StartDate?.split("T")[0] || "");
            setEndDate(tracker?.EndDate?.split("T")[0] || "");
            setExistingMedia(tracker?.Media || []);
        }
    }, [tracker]);

    const handleImageChange = (e) => {
        setImage((prev) => [...prev, ...Array.from(e.target.files)]);
        setVideo([]);
    };

    const handleVideoChange = (e) => {
        setVideo((prev) => [...prev, ...Array.from(e.target.files)]);
        setImage([]);
    };

    const handleRemoveFile = (idx, type) => {
        if (type === "image") {
            setImage((prev) => prev.filter((_, i) => i !== idx));
        } else {
            setVideo((prev) => prev.filter((_, i) => i !== idx));
        }
    };

    const handleRemoveExistingMedia = (id) => {
        setExistingMedia((prev) => prev.filter((m) => m._id !== id));
    };

    const UpdateTracker = async () => {
        if (!Title.trim()) {
            toast.error("Title is required");
            return;
        }
        if (!Description.trim()) {
            toast.error("Description is required");
            return;
        }
        if (!Status) {
            toast.error("Please select a status");
            return;
        }
        if (!StartDate) {
            toast.error("Start Date is required");
            return;
        }
        if (!EndDate) {
            toast.error("End Date is required");
            return;
        }
        if (new Date(StartDate) > new Date(EndDate)) {
            toast.error("End Date cannot be earlier than Start Date");
            return;
        }

        try {
            const formData = new FormData();
            formData.append("Title", Title);
            formData.append("Description", Description);
            formData.append("Status", Status);
            formData.append("StartDate", StartDate);
            formData.append("EndDate", EndDate);

            Image.forEach((file) => {
                formData.append("Image", file);
            });

            Video.forEach((file) => {
                formData.append("Video", file);
            });
            const mediaIdsToKeep = ExistingMedia.map((m) => m._id);
            formData.append("KeepMedia", JSON.stringify(mediaIdsToKeep));

            setLoading(true);
            const res = await apiputFormData(`api/contractor/UpdateRequirementTracker/${tracker?._id}`,formData,);

            toast.success(res?.data?.message || "Tracker updated successfully");
            fetchAllTracker();
            onClose();
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Error updating tracker");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-bold mb-4">Update Tracker</h2>

                <div className="space-y-4">
                    <input
                        type="text"
                        placeholder="Title"
                        value={Title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full border rounded p-2"
                    />
                    <textarea
                        placeholder="Description"
                        value={Description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full border rounded p-2"
                    />
                    <select
                        value={Status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="w-full border rounded p-2"
                    >
                        <option value="">Select Status</option>
                        <option value="Pending">Pending</option>
                        <option value="Ongoing">Ongoing</option>
                        <option value="Completed">Completed</option>
                    </select>
                    <div className="flex gap-2">
                        <input
                            type="date"
                            value={StartDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-1/2 border rounded p-2"
                        />
                        <input
                            type="date"
                            value={EndDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-1/2 border rounded p-2"
                        />
                    </div>
                    {ExistingMedia?.length > 0 && (
                        <div>
                            <p className="text-sm font-semibold mb-2">Existing Media</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {ExistingMedia.map((m) => (
                                    <div
                                        key={m._id}
                                        className="relative group rounded-lg border shadow overflow-hidden"
                                    >
                                        {m.FileType === "image" ? (
                                            <img
                                                src={m.FileUrl}
                                                alt="media"
                                                className="w-full h-32 object-cover"
                                            />
                                        ) : (
                                            <video
                                                src={m.FileUrl}
                                                controls
                                                className="w-full h-32 object-cover bg-black"
                                            />
                                        )}
                                        <button
                                            onClick={() => handleRemoveExistingMedia(m._id)}
                                            className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                                        >
                                            <Trash size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {!Image.length && !Video.length && (
                        <div className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 text-gray-500">
                            <p className="mb-2">Choose what to upload</p>
                            <div className="flex gap-4">
                                <label className="cursor-pointer px-4 py-2 bg-blue-100 text-blue-600 rounded hover:bg-blue-200">
                                    Upload Images
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="hidden"
                                    />
                                </label>
                                <label className="cursor-pointer px-4 py-2 bg-green-100 text-green-600 rounded hover:bg-green-200">
                                    Upload Videos
                                    <input
                                        type="file"
                                        multiple
                                        accept="video/*"
                                        onChange={handleVideoChange}
                                        className="hidden"
                                    />
                                </label>
                            </div>
                        </div>
                    )}

                    {(Image?.length > 0 || Video?.length > 0) && (
                        <div>
                            <p className="text-sm font-semibold text-gray-800 mb-2">
                                New {Image?.length > 0 ? "Images" : "Videos"}
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {Image?.map((file, idx) => (
                                    <div
                                        key={idx}
                                        className="relative group rounded-lg border shadow overflow-hidden"
                                    >
                                        <img
                                            src={URL.createObjectURL(file)}
                                            alt="preview"
                                            className="w-full h-32 object-cover"
                                        />
                                        <button
                                            onClick={() => handleRemoveFile(idx, "image")}
                                            className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                                {Video?.map((file, idx) => (
                                    <div
                                        key={idx}
                                        className="relative group rounded-lg border shadow overflow-hidden"
                                    >
                                        <video
                                            src={URL.createObjectURL(file)}
                                            controls
                                            className="w-full h-32 object-cover bg-black"
                                        />
                                        <button
                                            onClick={() => handleRemoveFile(idx, "video")}
                                            className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={UpdateTracker}
                        disabled={Loading}
                        className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                    >
                        {Loading ? "Updating..." : "Update"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ContractorUpdateTrackerModal;
